<template>
  <nav :class="$style.mobileNav">
    <!-- Category Menu (shown items) -->
    <CategoryMenuToolbar
      :activeCategoryName="activeCategory.title"
      :class="$style.mobileMenu"
      @itemClick="navigate"
      :menu="shownMenuItems"
      @menuControlClick="toggleDrawer"
    />
    <!-- Category Menu (hidden items) -->
    <Drawer :class="[$style.slideoutMenu, $style.drawer]" :isActive="drawerIsActive">
      <div :class="$style.header">
        <h2>Menu</h2>
        <button :class="$style.rightCta" @click="toggleDrawer(false)">
          <IconOfType :class="$style.icon" name="mdi-close" />
        </button>
      </div>
      <div :class="$style.body">
        <UserCredits :class="$style.bodyHeader" />
        <hr />
        <List :class="$style.itemGroup" keyName="name" :list="hiddenMenuItems">
          <template #default="{ item }">
            <button :class="[$style.item, $style.menuItem]" @click="selectMenuItem(item)">
              <IconOfType :class="$style.icon" :name="item.icon" />
              <span :class="$style.label">{{ item.title }}</span>
              <IconOfType :class="$style.icon" name="mdi-chevron-right" />
            </button>
          </template>
        </List>
      </div>
    </Drawer>

    <!-- Category Submenu -->
    <Drawer :class="$style.drawer" :isActive="subDrawerIsActive">
      <!-- Drawer Header -->
      <div :class="$style.header">
        <button :class="[$style.back, $style.leftCta]" @click="subDrawerIsActive = false">
          <IconOfType :class="$style.icon" name="mdi-chevron-left" />
          Back
        </button>
        <h3>{{ activeCategory.title }}</h3>
        <button :class="$style.rightCta" @click="toggleDrawer(false)">
          <IconOfType :class="$style.icon" name="mdi-close" />
        </button>
      </div>

      <!-- Drawer Body -->
      <div :class="$style.body">
        <List :class="$style.itemGroup" :list="activeSubmenu">
          <template #default="{ item }">
            <CategorySubmenuItem
              :class="$style.item"
              :item="item"
              @itemClick="navigate"
            />
          </template>
        </List>
      </div>
    </Drawer>
  </nav>
</template>

<script>
import Drawer from 'coreLib/vue/ui/Drawer';
import Link from 'coreLib/vue/ui/Link';
import IconOfType from 'coreLib/vue/ui/IconOfType';
import List from 'coreLib/vue/ui/List';

import UserCredits from '@/components/atoms/UserCredits';
import CategoryMenuToolbar from '@/components/molecules/CategoryMenuToolbar';
import CategorySubmenuItem from '@/components/molecules/CategorySubmenuItem';

export default {
  name: 'MobileNav',
  components: {
    Link,
    CategoryMenuToolbar,
    CategorySubmenuItem,
    Drawer,
    IconOfType,
    List,
    UserCredits
  },
  data() {
    return {
      activeCategory: this.defaultCategory,
      drawerIsActive: false,
      subDrawerIsActive: false
    };
  },
  props: {
    defaultCategory: {
      default: () => ({}),
      required: false,
      type: Object
    },
    isLoggedIn: {
      required: false,
      type: Boolean
    },
    menu: {
      default: () => [],
      type: Array
    },
    // Limits the shown menu categories in a condensed layout (mobile.)
    shownLimit: {
      default: 0,
      required: false,
      type: Number
    }
  },
  computed: {
    activeSubmenu() {
      // In menu.json for account, we are targeting account.items, some of the items won't have `loggedOut` as a property, which means
      // those items will be visible regardless of the user log-in state.
      return (this.activeCategory.items || []).filter(
        item =>
          item.loggedOut === undefined || this.isLoggedIn !== item.loggedOut
      );
    },
    hiddenMenuItems() {
      return this.menu.slice(this.shownLimit);
    },
    shownMenuItems() {
      return this.menu.slice(0, this.shownLimit);
    }
  },

  methods: {
    // @TODO Shared logic as a composable using Composition API.
    // Dynamically called on click as defined by `menu.json`.
    async logout() {
      const data = await this.$authService.logout();
      const loggedOutUrl = '/';

      if (data && !data.error && this.$route.path !== loggedOutUrl) {
        // Reset the active menu item before logging out.
        this.selectMenuItem(this.defaultMenuItem);
        this.$router.push(loggedOutUrl);
      }
    },
    // @TODO Shared logic as a composable using Composition API.
    async navigate(item, $event) {
      this.toggleDrawer(false);

      if (item.click) {
        // Call a method on the component instead of navigating.
        $event.preventDefault();
        this[item.click](item);
      }
    },

    // @TODO Shared logic as a composable using Composition API.
    selectMenuItem(item) {
      // Set the selected menu item as the active category - persist to local state & session storage (handles reloads.)
      window.sessionStorage.setItem('activeMenuItem', item.name);
      this.activeCategory = item;
      // @TODO Not to be included in the composable (mentioned in above TODO.)
      this.subDrawerIsActive = true;
    },
    // @TODO Shared logic as a composable using Composition API.
    toggleDrawer(active = null) {
      this.drawerIsActive = active === null ? !this.drawerIsActive : !!active;

      if (!this.drawerIsActive) {
        this.subDrawerIsActive = false;
      }
    }
  }
};
</script>

<style module>
.mobile-nav {
  background-color: $neutralDarkColor;
  height: auto;
}

.mobile-menu {
  border-top: 1px solid $primaryContrast;
  justify-content: space-between;

  > :first-child {
    order: 1;
  }

  i,
  svg {
    font-size: 28px;
  }

  li {
    flex: 100%;
  }

  li > * {
    height: 62px;
    // Accomodates mobile browser elements which would otherwise cover the nav.
    margin-bottom: 13px;
  }

  li:hover {
    background-color: $primaryContrastAlpha1;
  }
}

.drawer {
  background-color: $neutralDarkColor;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 76px);
  left: 100vw;
  width: 100%;

  .body,
  .body-header,
  .header {
    align-items: center;
    display: flex;
    flex: 0;
    flex-direction: column;
  }

  .header {
    flex-direction: row;
    justify-content: center;
    padding: $spacing-7 $spacing-8;
    position: relative;

    button {
      @include button-base;
      font-weight: normal;
      height: 100%;
      padding: $spacing-8;
      position: absolute;
    }

    h2,
    h3 {
      font-size: 28px;
      font-weight: bold;
      overflow: hidden;
      margin: 0 78px;
      text-overflow: ellipsis;
    }

    .left-cta {
      left: 0;
    }

    .right-cta {
      right: 0;
    }
  }

  .body {
    @include scrollbar-standard($primaryContrast);
    border-top: 1px solid $primaryContrast;
    flex: 100%;
    order: 1;
    overflow: hidden;
    overflow-y: auto;
    padding: 0;
  }

  .body-header {
    font-size: 20px;
    padding: $spacing-6 $spacing-8;
    width: 100%;

    > * + * {
      margin-top: $spacing-5;
    }

    > a {
      width: 100%;
    }
  }

  // @TODO Move into Divider component.
  hr {
    border: 0 solid $lightColorAlpha3;
    border-top-width: 1px;
    width: calc(100% - $spacing-8 * 2);
  }
}

.item-group {
  width: 100%;
}

.item {
  font-size: 24px;
  font-weight: 500;
  line-height: 1.2;
  padding: $spacing-5 $spacing-8;

  &:hover {
    background-color: $lightColorAlpha4;
  }
}

.menu-item {
  align-items: center;
  display: flex;
  gap: $spacing-6;
  padding: $spacing-8;
  text-align: left;
  width: 100%;
}

.label {
  flex: auto;
}

.icon {
  font-size: 24px;
}
</style>
